import { Select } from "antd";
import React, { useEffect } from "react";
import styled from "styled-components";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation } from "react-router-dom";

import { Button, Input } from "components";
import { SelectIcon } from "icons";
import { VerifyPhoneNumberActionCreators } from "redux/rootActions";
import { verifyPhoneNumberDataResponse } from "redux/setting/verifyPhoneNumber/verifyPhoneNumberStates";
import { httpStatus } from "configs/httpStatus";

import { phoneCode } from "./phone-code";
import { Title } from "./NursingHomeRegistration";

const { Option } = Select;

const WrapperCodeNFlag = styled.div`
  display: flex;
  gap: 4.5px !important;
  align-items: center;

  img {
    width: fit-content;
  }
`;

const WrapperInput = styled.div`
  position: absolute;
  left: 99px;
  width: calc(100% - 100px);

  input {
    border-left: unset;
    border-radius: 0 6px 6px 0;

    &:focus {
      border: 1px solid #cccccc;
      border-left: unset;
      border-radius: 0 6px 6px 0;
    }
  }
`;

const WrapperSelect = styled.div`
  border: 1px solid #cccccc;
  border-right: unset;
  border-radius: 6px;
`;

const Label = styled.div`
  font-family: Noto Sans JP;
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0px;
  color: #3b3b3b;
  margin-top: 32px;
  margin-bottom: 8px;
`;

const SelectStyled = styled(Select)<any>(({ theme, fs, mode, right }) => ({
  width: 105,
  // position: "absolute",
  ".ant-select-selector": {
    alignItems: "center",
    height: "38px!important",
    boxShadow: "inset 0px 2px 4px rgba(0, 0, 0, 0.08)",
    borderRadius: `6px 0 0 6px!important`,
    padding: mode ? "5px 30px 5px 12px" : "0 12px!important",
    backgroundColor: `#F2F2F2!important`,
    background: `#F2F2F2!important`,
  },
  ".ant-select-arrow": {
    top: "44%",
    right: right || "18px",
  },
  ".ant-select-selection-overflow .ant-select-selection-item": {
    border: "none",
    borderRadius: 4,
    background: theme.colors.background,
    fontSize: fs || theme.sizes.xs,
    fontWeight: theme.fontWeight.medium,
    lineHeight: "16px",
    padding: "3px 4px 4px",
    marginRight: 8,
  },
  ".ant-select-selection-item": {
    fontWeight: 500,
    height: 40,
    paddingTop: "6px!important",
  },
}));

const schema = yup.object().shape({
  phoneNumber: yup
    .string()
    .trim()
    .required("携帯電話番号入力してください")
    .matches(/[0]?[0-9]{9,}$/, "有効な携帯電話番号を入力してください"),
});

const RegistrationByPhoneNumber: React.FC = () => {
  const { search } = useLocation();
  const superCityName = new URLSearchParams(search).get("name");
  const superCityCode = new URLSearchParams(search).get("super-city-code");

  const dispatch = useDispatch();
  const verifyPhoneNumberData = useSelector(verifyPhoneNumberDataResponse);
  const { handleSubmit, control, setValue, setError, watch } = useForm({
    resolver: yupResolver(schema),
  });

  const phoneNumberWatch = watch("phoneNumber");

  const trimPhoneNumber = (phone: any) => {
    if (!phone) {
      return "";
    } else {
      if (phone.startsWith("0")) {
        return phone.slice(1);
      } else {
        return phone;
      }
    }
  };

  useEffect(() => {
    if (verifyPhoneNumberData) {
      if (verifyPhoneNumberData.status === httpStatus.StatusOK) {
        const searchParams = `${window.location.search}&phone=${verifyPhoneNumberData.result.mobile_number}`;
        dispatch(VerifyPhoneNumberActionCreators.handleResetAction());

        if (verifyPhoneNumberData.result.is_verified) {
          localStorage.setItem("type_form", "short");
          window.location.replace(`/account-registration${searchParams}`);
        } else {
          window.location.replace(`/verify-otp${searchParams}`);
        }
      } else {
        if (
          verifyPhoneNumberData.result === "mobile_number_has_been_registered"
        ) {
          setError("phoneNumber", {
            message: "この番号は他の自治体で登録済みです",
          });
        } else {
          setError("phoneNumber", {
            message: "もう一度試してください",
          });
        }
      }
    }
  }, [verifyPhoneNumberData]);

  useEffect(() => {
    setValue("code", "+81");
    setError("phoneNumber", {
      message: "",
    });
  }, []);

  useEffect(() => {
    setError("phoneNumberWatch", { message: "" });
  }, [phoneNumberWatch]);

  const onSubmit = (formData: any) => {
    dispatch(
      VerifyPhoneNumberActionCreators.verifyPhoneNumberAction({
        mobile_number: formData.code + trimPhoneNumber(formData.phoneNumber),
        super_city_code: superCityCode as any,
      })
    );
  };

  return (
    <div>
      <Title>{superCityName}登録</Title>

      <Label>現在ご利用中の携帯電話番号</Label>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div
          style={{
            position: "relative",
            marginBottom: 32,
            display: "flex",
          }}
        >
          <Controller
            control={control}
            name="code"
            render={({ field: { onChange } }) => (
              <WrapperSelect>
                <SelectStyled
                  bordered={false}
                  defaultValue="+81"
                  suffixIcon={<SelectIcon />}
                  onChange={onChange}
                >
                  {phoneCode.map((option: any) => (
                    <Option
                      value={option.phoneCode}
                      key={option.code}
                      label={option.label}
                      disabled={option.disabled}
                    >
                      <WrapperCodeNFlag>
                        <img
                          src={`https://flagsapi.com/${option.code}/flat/64.png`}
                          width={24}
                          height={16}
                        />
                        <div>{option.phoneCode}</div>
                      </WrapperCodeNFlag>
                    </Option>
                  ))}
                </SelectStyled>
              </WrapperSelect>
            )}
          />

          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <WrapperInput>
                <Input
                  height={40}
                  fwLabel={500}
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              </WrapperInput>
            )}
          />
        </div>
        <Button
          name="登　録"
          background="#2F8CAE"
          color="#FFFFFF"
          border="none"
          fontSize={16}
          fontWeight={700}
          padding="7.5px 16px"
          width="100%"
        />
      </form>
    </div>
  );
};

export default RegistrationByPhoneNumber;
